/* eslint @typescript-eslint/no-var-requires: "off" */
import 'jquery/dist/jquery.min';
import 'select2/dist/js/select2.full.min';
import 'select2/dist/css/select2.min.css';
import $ from 'jquery';
import 'select2';
import {defineComponent, onMounted, onUnmounted, PropType, ref, watch} from '@vue/composition-api';
import type {DataFormat, GroupedDataFormat, OptGroupData, OptionData, Options, SearchOptions, Translation} from "select2";
import stringify from "json-stable-stringify";
import {i18n} from "@/utilities/vue-i18n";
import {Key} from "ts-key-enum";

const languages: Record<string, Translation> = {
    'en': require('select2/src/js/select2/i18n/en.js'),
    'sv': require('select2/src/js/select2/i18n/sv.js'),
    'da': require('select2/src/js/select2/i18n/da.js'),
    'nb': require('select2/src/js/select2/i18n/nb.js'),
    'no': require('select2/src/js/select2/i18n/nb.js'),
    'fi': require('select2/src/js/select2/i18n/fi.js')
};

interface HtmlOptions {
    enabled: boolean;
    selectionSelector?: string;
    plainTextSelection?: boolean;
}

export default defineComponent({
    props: {
        options: Array as PropType<Array<DataFormat> | Array<GroupedDataFormat>>,
        value: [String, Number, Object] as PropType<DataFormat | string | number>,
        label: String,
        select2Options: {
            type: Object as PropType<Options>,
            default: () => ({}),
            required: false,
        },
        htmlOptions: Object as PropType<HtmlOptions>,
        searchHint: {
            type: String,
            default: undefined,
            required: false,
        },
        numeric: {
            type: Boolean,
            default: true,
            required: false,
        },
        selectObject: {
            type: Boolean,
            default: false,
            required: false,
        },
    },
    setup(props, context) {
        const language = Object.keys(languages).find(lang => lang === i18n.locale) ? languages[i18n.locale] : languages['en'];
        const el = ref<HTMLSelectElement>();
        let select: JQuery<HTMLElement>;
        const htmlStripper = document.createElement('div') as Element;
        const titleHtmlStripper = document.createElement('div') as Element;
        const matcher = (params: SearchOptions, data: OptGroupData | OptionData): OptGroupData | OptionData | null => {
            if (typeof(params.term) === 'undefined' || params.term.trim() === '') {
                return data;
            }
            if (typeof (data.text) === 'undefined') {
                return null;
            }
            const text = data.text.toLowerCase();
            const parts = params.term.split(' ').filter(split => split.trim() !== '' && split.length >= 1).map(part => part.toLowerCase()).sort(part => part.length);
            if (parts.every(part => text.indexOf(part) >= 0)) {
                return {...data};
            }
            return null;
        };
        const stripHtml = (html: string) => {
            htmlStripper.innerHTML = html;
            return htmlStripper.textContent ?? '';
        }
        const stripTitleHtml = (html: string) => {
            const titleSelector = props.htmlOptions?.selectionSelector && props.htmlOptions?.selectionSelector?.trim() !== '';
            titleHtmlStripper.innerHTML = html;
            const node = (titleSelector ? titleHtmlStripper.querySelector(props.htmlOptions?.selectionSelector as string) : titleHtmlStripper)?.cloneNode(true);
            if (props.htmlOptions?.plainTextSelection == null || props.htmlOptions?.plainTextSelection)
            {
                return node?.textContent ?? titleHtmlStripper.textContent ?? '';
            }
            else
            {
                return node == null ? $(`<div class="select2-results__option-content">${html}</div>`) : $(node ?? `<div class="select2-results__option-content"></div>`) as JQuery<HTMLElement>;
            }
        }
        const keyHandler: JQuery.TypeEventHandler<HTMLElement, undefined, HTMLElement, HTMLElement, string> = function (e) {
            if ((e.key === Key.ArrowUp || e.key === Key.ArrowDown) && !e.altKey)
            {
                const selectElement = (select.data('select2') as unknown as Record<string, JQuery<HTMLSelectElement>>).$element
                let newValue;
                const selection = selectElement.find('option:selected');
                if (e.key === Key.ArrowDown) {
                    newValue = selection.length ? selection.nextAll(':enabled').first().val() : selectElement.find('option:enabled').first().val();
                } else if (e.key === Key.ArrowUp) {
                    newValue = selection.length ? selection.prevAll(':enabled').first().val() : selectElement.find('option:enabled').last().val();
                }
                if (newValue != undefined) {
                    selectElement.val(newValue);
                    context.emit('input', props.numeric ? +newValue : newValue);
                }
            }
        };
        const setupSelect2 = () => {
            const select2Options: Options = {
                width: '',
                escapeMarkup: props.htmlOptions?.enabled ? (markup) => stripHtml(markup) : (markup) => markup,
                templateResult: (result) => (props.htmlOptions && props.htmlOptions?.enabled !== false ? $(`<div class="select2-results__option-content">${result.text}</div>`) : result.text),
                templateSelection: (selection) => props.htmlOptions && props.htmlOptions?.enabled !== false ? stripTitleHtml(selection.text) : selection.text,
                matcher: matcher,
                ...props.select2Options,
                data: (props.htmlOptions && props.htmlOptions?.enabled !== false) && props.options instanceof Array ? (props.options as unknown as Array<OptionData>).map(option => ({...option, title: stripTitleHtml(option.text)})) : props.options,
                language: language
            };

            select
                .empty()
                .select2(select2Options)
                .val(props.selectObject ? (props.value as DataFormat)?.id : props.value as (string | number))
                .trigger('change')
                .on('select2:select', function (e) {
                    context.emit('input', props.selectObject ? e.params.data : props.numeric ? +e.params.data.id : e.params.data.id);
                });
            select.data('select2').$selection.on('keyup', keyHandler);
        }
        function setSearchHint() {
            if (typeof (props.searchHint) === 'string') {
                select.data('select2').$dropdown.find('input[type=search]').trigger('focus').attr('placeholder', props.searchHint);
            }
        }
        onMounted(() => {
            select = $(el.value as HTMLElement);
            select.on("select2:open", function () {
                setSearchHint();
                setTimeout(() => select.data('select2').$dropdown.find('input[type=search]').trigger('focus'), 500);
            })
            setupSelect2();
        });

        watch(() => props.value, (value) => {
            select
                .val(props.selectObject ?
                        (value as DataFormat)?.id :
                        value as (string | number)
                    )
                .trigger('change');
        }, { deep: props.selectObject });

        watch(() => props.select2Options, (newValue, oldValue) => {
            if (stringify(newValue) !== stringify(oldValue)) {
                setupSelect2();
            }
        });
        watch(() => props.options, (newValue, oldValue) => {
            if (stringify(newValue) !== stringify(oldValue)) {
                setupSelect2();
            }
        });
        watch(() => props.searchHint, (newValue, oldValue) => {
            if (newValue !== oldValue) {
                setSearchHint();
            }
        });
        onUnmounted(() => {
            select.data('select2').$selection.off('keyup', keyHandler);
            (el.value as HTMLSelectElement).style.visibility = 'hidden';
            select
                .css('width', '100%')
                .off()
                .select2('destroy');
            $('body').off('keyup', keyHandler);
        });
        return {
            el
        }
    }
});
